import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronRightIcon, ImageIcon } from 'lucide-react';
import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Spinner } from '@nucleus-commerce/ui/components/spinner';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { ErrorState } from '../errors/error-state';

import type { PickerChildren, PickerRow } from './types';
import type { UsePickerSelectionResult } from './use-picker-selection';

/** Marks every row control so the arrow keys can move between them. */
export const CONTROL = 'data-picker-control';

function Thumbnail({ thumbnail }: { thumbnail: PickerRow['thumbnail'] }) {
  if (thumbnail === undefined) return null;
  return (
    <span className="bg-muted flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border">
      {thumbnail ? (
        <img
          src={thumbnail.url}
          alt={thumbnail.alt ?? ''}
          className="size-full object-cover"
          loading="lazy"
        />
      ) : (
        <ImageIcon className="text-muted-foreground size-4" aria-hidden />
      )}
    </span>
  );
}

export function RowBody({ row, children }: { row: PickerRow; children?: React.ReactNode }) {
  const reason = typeof row.disabled === 'string' ? row.disabled : null;
  return (
    <>
      <Thumbnail thumbnail={row.thumbnail} />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium">{row.title}</span>
          {row.badge}
        </span>
        {row.description ? (
          <span className="text-muted-foreground block truncate text-xs">{row.description}</span>
        ) : null}
      </span>
      {children}
      {reason ? (
        <span className="text-muted-foreground shrink-0 text-xs">{reason}</span>
      ) : row.meta ? (
        <span className="text-muted-foreground flex shrink-0 items-center gap-4 text-right text-sm tabular-nums">
          {row.meta}
        </span>
      ) : null}
    </>
  );
}

interface RowProps<TItem, TChild> {
  id: string;
  item: TItem;
  row: PickerRow;
  selection: UsePickerSelectionResult<TItem, TChild>;
  multiple: boolean | number;
}

export function ItemRow<TItem, TChild>({
  id,
  item,
  row,
  selection,
  multiple,
}: RowProps<TItem, TChild>) {
  const controlId = React.useId();
  const selected = selection.isSelected(id);
  const locked = Boolean(row.disabled);
  const checked = selected || (locked && row.checked === true);
  // A cap locks the rows not yet picked; one pick replaces, so it never locks.
  const disabled = locked || (!selected && selection.full && multiple !== false);
  return (
    <li>
      <label
        htmlFor={controlId}
        className={cn(
          'flex min-h-14 items-center gap-3 px-4 py-2 text-sm',
          disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted/50 cursor-pointer',
          selected && 'bg-primary/5',
        )}
      >
        <Checkbox
          id={controlId}
          {...{ [CONTROL]: '' }}
          checked={checked}
          disabled={disabled}
          aria-label={row.title}
          onCheckedChange={(value) => selection.toggle(id, item, value === true)}
        />
        <RowBody row={row} />
      </label>
    </li>
  );
}

export function ParentRow<TItem, TChild>({
  id,
  item,
  row,
  selection,
  multiple,
  childRows,
}: RowProps<TItem, TChild> & { childRows: PickerChildren<TItem, TChild> }) {
  const { t } = useTranslation('common');
  const controlId = React.useId();
  const queryClient = useQueryClient();
  const many = childRows.hasMany(item);
  const [expanded, setExpanded] = React.useState(false);
  const childQuery = childRows.query(item);
  // Expanding loads the children; so does ticking (below), which also fills this cache entry.
  const children = useQuery({
    queryKey: childQuery.queryKey,
    queryFn: ({ signal }) => childQuery.queryFn({ signal }),
    enabled: expanded,
    staleTime: 0,
  });
  const [loading, setLoading] = React.useState(false);

  const loaded = (children.data ?? []).map((child) => ({
    id: childRows.getId(child),
    child,
    row: childRows.getRow(child, item),
  }));
  const selectable = loaded.filter((entry) => !entry.row.disabled);
  const picked = loaded.filter((entry) => selection.isChildSelected(entry.id)).length;
  const state: boolean | 'indeterminate' =
    picked === 0 ? false : picked === selectable.length ? true : 'indeterminate';
  const locked = Boolean(row.disabled) || (children.isSuccess && selectable.length === 0);
  const disabled = locked || (picked === 0 && selection.full && multiple !== false);

  const toggleAll = async (checked: boolean) => {
    let rows: TChild[];
    if (children.data) {
      rows = children.data;
    } else {
      setLoading(true);
      try {
        rows = await queryClient.query({
          queryKey: childQuery.queryKey,
          queryFn: ({ signal }) => childQuery.queryFn({ signal }),
        });
      } catch {
        // The row expands to show the error and a retry.
        setExpanded(true);
        return;
      } finally {
        setLoading(false);
      }
    }
    const options = rows
      .map((child) => ({ id: childRows.getId(child), child }))
      .filter((entry) => !childRows.getRow(entry.child, item).disabled);
    // One pick takes the first variant; open the row to choose another.
    selection.toggleChildren(id, item, multiple === false ? options.slice(0, 1) : options, checked);
    if (many && multiple === false) setExpanded(true);
  };

  return (
    <li>
      <div
        className={cn(
          'flex min-h-14 items-center gap-3 px-4 py-2 text-sm',
          disabled ? 'opacity-60' : 'hover:bg-muted/50',
          state !== false && 'bg-primary/5',
        )}
      >
        {loading ? (
          <Spinner className="size-4" label={t('resourcePicker.loadingChildren')} />
        ) : (
          <Checkbox
            id={controlId}
            {...{ [CONTROL]: '' }}
            checked={state}
            disabled={disabled}
            aria-label={row.title}
            aria-expanded={many ? expanded : undefined}
            onCheckedChange={(value) => void toggleAll(value === true)}
            onKeyDown={(event) => {
              if (!many) return;
              if (event.key === 'ArrowRight' && !expanded) setExpanded(true);
              if (event.key === 'ArrowLeft' && expanded) setExpanded(false);
            }}
          />
        )}
        <label
          htmlFor={controlId}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-3',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          <RowBody row={row} />
        </label>
        {many ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            aria-label={t(expanded ? 'resourcePicker.collapse' : 'resourcePicker.expand', {
              title: row.title,
            })}
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? <ChevronDownIcon aria-hidden /> : <ChevronRightIcon aria-hidden />}
          </Button>
        ) : null}
      </div>
      {expanded && (many || children.isError) ? (
        children.isPending ? (
          <div className="flex flex-col gap-2 py-2 pr-4 pl-14">
            <Skeleton className="h-6" />
            <Skeleton className="h-6" />
          </div>
        ) : children.isError ? (
          <div className="py-2 pr-4 pl-14">
            <ErrorState error={children.error} onRetry={() => void children.refetch()} compact />
          </div>
        ) : (
          <ul className="border-t">
            {loaded.map((entry) => (
              <ChildRow
                key={entry.id}
                childId={entry.id}
                row={entry.row}
                checked={selection.isChildSelected(entry.id)}
                full={selection.full && multiple !== false}
                onToggle={(checked) =>
                  selection.toggleChildren(
                    id,
                    item,
                    [{ id: entry.id, child: entry.child }],
                    checked,
                  )
                }
              />
            ))}
          </ul>
        )
      ) : null}
    </li>
  );
}

export function ChildRow({
  childId,
  row,
  checked,
  full,
  onToggle,
}: {
  childId: string;
  row: PickerRow;
  checked: boolean;
  full: boolean;
  onToggle: (checked: boolean) => void;
}) {
  const controlId = React.useId();
  const disabled = Boolean(row.disabled) || (!checked && full);
  return (
    <li data-child-id={childId}>
      <label
        htmlFor={controlId}
        className={cn(
          'flex min-h-11 items-center gap-3 py-1.5 pr-4 pl-11 text-sm',
          disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted/50 cursor-pointer',
          checked && 'bg-primary/5',
        )}
      >
        <Checkbox
          id={controlId}
          {...{ [CONTROL]: '' }}
          checked={checked}
          disabled={disabled}
          aria-label={row.title}
          onCheckedChange={(value) => onToggle(value === true)}
        />
        <RowBody row={row} />
      </label>
    </li>
  );
}
